<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

const route = useRoute();
const { t, te } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getMyTeams');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');

const ROUTE_MAP = {
  // Inbox
  inbox_view: ['SIDEBAR.INBOX'],
  inbox_view_conversation: ['SIDEBAR.INBOX'],

  // Conversations
  home: ['SIDEBAR.CONVERSATIONS', 'SIDEBAR.ALL_CONVERSATIONS'],
  inbox_conversation: ['SIDEBAR.CONVERSATIONS', 'SIDEBAR.ALL_CONVERSATIONS'],
  conversation_mentions: [
    'SIDEBAR.CONVERSATIONS',
    'SIDEBAR.MENTIONED_CONVERSATIONS',
  ],
  conversation_through_mentions: [
    'SIDEBAR.CONVERSATIONS',
    'SIDEBAR.MENTIONED_CONVERSATIONS',
  ],
  conversation_unattended: [
    'SIDEBAR.CONVERSATIONS',
    'SIDEBAR.UNATTENDED_CONVERSATIONS',
  ],
  conversation_through_unattended: [
    'SIDEBAR.CONVERSATIONS',
    'SIDEBAR.UNATTENDED_CONVERSATIONS',
  ],
  inbox_dashboard: ['SIDEBAR.CONVERSATIONS', 'SIDEBAR.CHANNELS'],
  conversation_through_inbox: ['SIDEBAR.CONVERSATIONS', 'SIDEBAR.CHANNELS'],
  team_conversations: ['SIDEBAR.CONVERSATIONS', 'SIDEBAR.TEAMS'],
  conversations_through_team: ['SIDEBAR.CONVERSATIONS', 'SIDEBAR.TEAMS'],
  label_conversations: ['SIDEBAR.CONVERSATIONS', 'SIDEBAR.LABELS'],
  conversations_through_label: ['SIDEBAR.CONVERSATIONS', 'SIDEBAR.LABELS'],
  folder_conversations: [
    'SIDEBAR.CONVERSATIONS',
    'SIDEBAR.CUSTOM_VIEWS_FOLDER',
  ],
  conversations_through_folders: [
    'SIDEBAR.CONVERSATIONS',
    'SIDEBAR.CUSTOM_VIEWS_FOLDER',
  ],

  // Contacts
  contacts_dashboard_index: ['SIDEBAR.CONTACTS', 'SIDEBAR.ALL_CONTACTS'],
  contacts_dashboard_active: ['SIDEBAR.CONTACTS', 'SIDEBAR.ACTIVE'],
  contacts_dashboard_segments_index: [
    'SIDEBAR.CONTACTS',
    'SIDEBAR.CUSTOM_VIEWS_SEGMENTS',
  ],
  contacts_dashboard_labels_index: ['SIDEBAR.CONTACTS', 'SIDEBAR.TAGGED_WITH'],
  contacts_edit: ['SIDEBAR.CONTACTS'],
  contacts_edit_segment: ['SIDEBAR.CONTACTS', 'SIDEBAR.CUSTOM_VIEWS_SEGMENTS'],
  contacts_edit_label: ['SIDEBAR.CONTACTS', 'SIDEBAR.TAGGED_WITH'],

  // Reports
  account_overview_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_OVERVIEW'],
  conversation_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_CONVERSATION'],
  agent_reports_index: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_AGENT'],
  agent_reports_show: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_AGENT'],
  label_reports_index: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_LABEL'],
  inbox_reports_index: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_INBOX'],
  inbox_reports_show: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_INBOX'],
  team_reports_index: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_TEAM'],
  team_reports_show: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_TEAM'],
  round_robin_dashboard: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_ROUND_ROBIN'],
  csat_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.CSAT'],
  sla_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_SLA'],
  bot_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_BOT'],
  custom_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_CUSTOM'],

  // Captain
  captain_assistants_index: ['SIDEBAR.CAPTAIN', 'SIDEBAR.CAPTAIN_ASSISTANTS'],
  captain_documents_index: ['SIDEBAR.CAPTAIN', 'SIDEBAR.CAPTAIN_DOCUMENTS'],
  captain_responses_index: ['SIDEBAR.CAPTAIN', 'SIDEBAR.CAPTAIN_RESPONSES'],

  // Telephony
  telephony_dashboard: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_DASHBOARD'],
  telephony_trunks: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_TRUNKS'],
  telephony_trunk_new: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_TRUNKS'],
  telephony_trunk_edit: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_TRUNKS'],
  telephony_extensions: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_EXTENSIONS'],
  telephony_extension_new: [
    'SIDEBAR.TELEPHONY',
    'SIDEBAR.TELEPHONY_EXTENSIONS',
  ],
  telephony_extension_edit: [
    'SIDEBAR.TELEPHONY',
    'SIDEBAR.TELEPHONY_EXTENSIONS',
  ],
  telephony_dids: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_DIDS'],
  telephony_did_new: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_DIDS'],
  telephony_did_edit: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_DIDS'],
  telephony_queues: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_QUEUES'],
  telephony_queue_new: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_QUEUES'],
  telephony_queue_edit: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_QUEUES'],
  telephony_queue_members: ['SIDEBAR.TELEPHONY', 'SIDEBAR.TELEPHONY_QUEUES'],
  telephony_queue_routes: [
    'SIDEBAR.TELEPHONY',
    'SIDEBAR.TELEPHONY_QUEUE_ROUTES',
  ],
  telephony_queue_route_new: [
    'SIDEBAR.TELEPHONY',
    'SIDEBAR.TELEPHONY_QUEUE_ROUTES',
  ],
  telephony_queue_route_edit: [
    'SIDEBAR.TELEPHONY',
    'SIDEBAR.TELEPHONY_QUEUE_ROUTES',
  ],
  telephony_call_records: [
    'SIDEBAR.TELEPHONY',
    'SIDEBAR.TELEPHONY_CALL_RECORDS',
  ],
  telephony_ivr: ['SIDEBAR.TELEPHONY', 'URA Interativa'],
  telephony_ivr_builder_new: ['SIDEBAR.TELEPHONY', 'URA Interativa'],
  telephony_ivr_builder_edit: ['SIDEBAR.TELEPHONY', 'URA Interativa'],

  // WhatsApp Voice
  whatsapp_sip_extensions: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_EXTENSIONS',
  ],
  whatsapp_sip_extension_new: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_EXTENSIONS',
  ],
  whatsapp_sip_extension_edit: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_EXTENSIONS',
  ],
  whatsapp_sip_queues: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_QUEUES',
  ],
  whatsapp_sip_queue_new: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_QUEUES',
  ],
  whatsapp_sip_queue_edit: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_QUEUES',
  ],
  whatsapp_sip_dids: ['SIDEBAR.WHATSAPP_VOICE', 'SIDEBAR.WHATSAPP_VOICE_DIDS'],
  whatsapp_sip_did_new: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_DIDS',
  ],
  whatsapp_sip_did_edit: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_DIDS',
  ],
  whatsapp_sip_trunks: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_TRUNKS',
  ],
  whatsapp_sip_trunk_new: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_TRUNKS',
  ],
  whatsapp_sip_trunk_edit: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_TRUNKS',
  ],
  whatsapp_calling_ivr: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_IVR',
  ],
  whatsapp_calling_ivr_builder_new: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_IVR',
  ],
  whatsapp_calling_ivr_builder_edit: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_IVR',
  ],
  whatsapp_sip_holidays: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_HOLIDAYS',
  ],
  whatsapp_sip_holiday_new: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_HOLIDAYS',
  ],
  whatsapp_sip_holiday_edit: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_HOLIDAYS',
  ],
  whatsapp_sip_call_reports: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_CALL_REPORTS',
  ],
  whatsapp_sip_autodialer_ai_campaigns: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_AUTODIALER',
  ],
  whatsapp_sip_supervisor: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_SUPERVISOR',
  ],
  whatsapp_sip_supervisor_agents: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_SUPERVISOR',
    'SIDEBAR.WHATSAPP_VOICE_SUPERVISOR_AGENTS',
  ],
  whatsapp_sip_supervisor_live_calls: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_SUPERVISOR',
    'SIDEBAR.WHATSAPP_VOICE_SUPERVISOR_LIVE_CALLS',
  ],
  whatsapp_sip_supervisor_metrics: [
    'SIDEBAR.WHATSAPP_VOICE',
    'SIDEBAR.WHATSAPP_VOICE_SUPERVISOR',
    'SIDEBAR.WHATSAPP_VOICE_SUPERVISOR_METRICS',
  ],

  // Voice AI
  // Studio (AI Studio)
  studio_settings: ['AI Studio', 'Configurações'],
  studio_dashboard: ['AI Studio', 'Dashboard'],
  studio_agent_edit: ['AI Studio', 'Agentes'],
  // Builder de fluxo: aberto SEMPRE a partir de um projeto (/studio/flows
  // redireciona pra lista de projetos) — "Fluxos" não existe no menu lateral,
  // o breadcrumb honesto é Projetos; o nome do projeto/fluxo fica no topbar.
  studio_flow_builder_new: ['AI Studio', 'Projetos'],
  studio_flow_builder_edit: ['AI Studio', 'Projetos'],
  text_ai_flow_builder_new: ['AI Studio', 'Projetos'],
  text_ai_flow_builder_edit: ['AI Studio', 'Projetos'],
  studio_projects: ['AI Studio', 'Projetos'],
  studio_project_detail: ['AI Studio', 'Projetos'],
  studio_project_playground: ['AI Studio', 'Playground'],
  studio_templates: ['AI Studio', 'Templates'],
  studio_template_edit: ['AI Studio', 'Templates'],
  studio_analytics: ['AI Studio', 'Analytics'],
  studio_analytics_overview: ['AI Studio', 'Analytics'],
  studio_analytics_charts: ['AI Studio', 'Analytics'],
  studio_analytics_detail: ['AI Studio', 'Analytics'],

  // Kanban
  kanban_board: ['SIDEBAR.KANBAN', 'SIDEBAR.KANBAN_BOARD'],
  kanban_analytics: ['SIDEBAR.KANBAN', 'SIDEBAR.KANBAN_ANALYTICS'],
  kanban_scheduled_actions: [
    'SIDEBAR.KANBAN',
    'SIDEBAR.KANBAN_SCHEDULED_ACTIONS',
  ],

  // Campaigns - WhatsApp App
  campaigns_whatsapp_app_home: ['SIDEBAR.CAMPAIGNS', 'SIDEBAR.WHATSAPP_APP'],
  campaigns_whatsapp_app_schedule: [
    'SIDEBAR.CAMPAIGNS',
    'SIDEBAR.WHATSAPP_APP',
    'SIDEBAR.WHATSAPP_APP_SCHEDULE',
  ],
  campaigns_whatsapp_app_templates: [
    'SIDEBAR.CAMPAIGNS',
    'SIDEBAR.WHATSAPP_APP',
    'SIDEBAR.WHATSAPP_APP_TEMPLATES',
  ],

  // Campaigns - WhatsApp Official
  campaigns_whatsapp_official_home: [
    'SIDEBAR.CAMPAIGNS',
    'SIDEBAR.WHATSAPP_OFFICIAL',
  ],
  campaigns_whatsapp_official_home_legacy: [
    'SIDEBAR.CAMPAIGNS',
    'SIDEBAR.WHATSAPP_OFFICIAL',
  ],
  campaigns_whatsapp_official_schedule: [
    'SIDEBAR.CAMPAIGNS',
    'SIDEBAR.WHATSAPP_OFFICIAL',
    'SIDEBAR.WHATSAPP_OFFICIAL_SCHEDULE',
  ],
  campaigns_whatsapp_official_create_template: [
    'SIDEBAR.CAMPAIGNS',
    'SIDEBAR.WHATSAPP_OFFICIAL',
    'SIDEBAR.WHATSAPP_OFFICIAL_CREATE_TEMPLATE',
  ],
  campaigns_whatsapp_official_builder: [
    'SIDEBAR.CAMPAIGNS',
    'SIDEBAR.WHATSAPP_OFFICIAL',
    'SIDEBAR.WHATSAPP_OFFICIAL_BUILDER',
  ],

  // Help Center / Portals
  portals_index: ['SIDEBAR.HELP_CENTER.TITLE'],
  portals_articles_index: [
    'SIDEBAR.HELP_CENTER.TITLE',
    'SIDEBAR.HELP_CENTER.ARTICLES',
  ],
  portals_articles_new: [
    'SIDEBAR.HELP_CENTER.TITLE',
    'SIDEBAR.HELP_CENTER.ARTICLES',
  ],
  portals_articles_edit: [
    'SIDEBAR.HELP_CENTER.TITLE',
    'SIDEBAR.HELP_CENTER.ARTICLES',
  ],
  portals_categories_index: [
    'SIDEBAR.HELP_CENTER.TITLE',
    'SIDEBAR.HELP_CENTER.CATEGORIES',
  ],
  portals_categories_articles_index: [
    'SIDEBAR.HELP_CENTER.TITLE',
    'SIDEBAR.HELP_CENTER.CATEGORIES',
  ],
  portals_categories_articles_edit: [
    'SIDEBAR.HELP_CENTER.TITLE',
    'SIDEBAR.HELP_CENTER.CATEGORIES',
  ],
  portals_locales_index: [
    'SIDEBAR.HELP_CENTER.TITLE',
    'SIDEBAR.HELP_CENTER.LOCALES',
  ],
  portals_settings_index: [
    'SIDEBAR.HELP_CENTER.TITLE',
    'SIDEBAR.HELP_CENTER.SETTINGS',
  ],

  // Settings
  settings_home: ['SIDEBAR.SETTINGS'],
  general_settings_index: ['SIDEBAR.SETTINGS', 'SIDEBAR.ACCOUNT_SETTINGS'],
  agent_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.AGENTS'],
  settings_teams_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.TEAMS'],
  settings_teams_new: ['SIDEBAR.SETTINGS', 'SIDEBAR.TEAMS'],
  settings_teams_edit: ['SIDEBAR.SETTINGS', 'SIDEBAR.TEAMS'],
  settings_teams_finish: ['SIDEBAR.SETTINGS', 'SIDEBAR.TEAMS'],
  settings_teams_edit_finish: ['SIDEBAR.SETTINGS', 'SIDEBAR.TEAMS'],
  settings_teams_add_agents: ['SIDEBAR.SETTINGS', 'SIDEBAR.TEAMS'],
  settings_teams_edit_members: ['SIDEBAR.SETTINGS', 'SIDEBAR.TEAMS'],
  settings_inbox_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.INBOXES'],
  settings_inbox_new: ['SIDEBAR.SETTINGS', 'SIDEBAR.INBOXES'],
  settings_inbox_show: ['SIDEBAR.SETTINGS', 'SIDEBAR.INBOXES'],
  settings_inbox_finish: ['SIDEBAR.SETTINGS', 'SIDEBAR.INBOXES'],
  settings_inboxes_add_agents: ['SIDEBAR.SETTINGS', 'SIDEBAR.INBOXES'],
  settings_inboxes_page_channel: ['SIDEBAR.SETTINGS', 'SIDEBAR.INBOXES'],
  labels_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.LABELS'],
  labels_wrapper: ['SIDEBAR.SETTINGS', 'SIDEBAR.LABELS'],
  attributes_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.CUSTOM_ATTRIBUTES'],
  automation_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.AUTOMATION'],
  agent_bots: ['SIDEBAR.SETTINGS', 'SIDEBAR.AGENT_BOTS'],
  macros_wrapper: ['SIDEBAR.SETTINGS', 'SIDEBAR.MACROS'],
  macros_new: ['SIDEBAR.SETTINGS', 'SIDEBAR.MACROS'],
  macros_edit: ['SIDEBAR.SETTINGS', 'SIDEBAR.MACROS'],
  canned_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.CANNED_RESPONSES'],
  settings_applications: ['SIDEBAR.SETTINGS', 'SIDEBAR.INTEGRATIONS'],
  settings_applications_integration: [
    'SIDEBAR.SETTINGS',
    'SIDEBAR.INTEGRATIONS',
  ],
  settings_integrations: ['SIDEBAR.SETTINGS', 'SIDEBAR.INTEGRATIONS'],
  settings_integrations_dashboard_apps: [
    'SIDEBAR.SETTINGS',
    'SIDEBAR.INTEGRATIONS',
  ],
  settings_integrations_linear: ['SIDEBAR.SETTINGS', 'SIDEBAR.INTEGRATIONS'],
  settings_integrations_notion: ['SIDEBAR.SETTINGS', 'SIDEBAR.INTEGRATIONS'],
  settings_integrations_shopify: ['SIDEBAR.SETTINGS', 'SIDEBAR.INTEGRATIONS'],
  settings_integrations_slack: ['SIDEBAR.SETTINGS', 'SIDEBAR.INTEGRATIONS'],
  settings_integrations_webhook: ['SIDEBAR.SETTINGS', 'SIDEBAR.INTEGRATIONS'],
  auditlogs_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.AUDIT_LOGS'],
  custom_roles_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.CUSTOM_ROLES'],
  sla_list: ['SIDEBAR.SETTINGS', 'SIDEBAR.SLA'],
  sla_wrapper: ['SIDEBAR.SETTINGS', 'SIDEBAR.SLA'],
  billing_settings_index: ['SIDEBAR.SETTINGS', 'SIDEBAR.BILLING'],
  profile_settings: ['SIDEBAR_ITEMS.PROFILE_SETTINGS'],
  profile_settings_index: ['SIDEBAR_ITEMS.PROFILE_SETTINGS'],

  // Reports wrappers/show
  agent_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_AGENT'],
  inbox_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_INBOX'],
  label_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_LABEL'],
  label_reports_show: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_LABEL'],
  team_reports: ['SIDEBAR.REPORTS', 'SIDEBAR.REPORTS_TEAM'],

  // Notifications
  notifications_index: ['SIDEBAR.INBOX'],

  // Search
  search: ['COMBOBOX.SEARCH_PLACEHOLDER'],
};

const findById = (list, id) =>
  list.value.find(item => String(item.id) === String(id));

const resourceLabel = computed(() => {
  const name = route.name;
  const params = route.params || {};

  switch (name) {
    case 'inbox_dashboard':
    case 'conversation_through_inbox':
    case 'settings_inbox_show':
    case 'settings_inbox_finish':
    case 'settings_inboxes_add_agents':
    case 'settings_inboxes_page_channel':
    case 'inbox_reports_show':
      return findById(inboxes, params.inbox_id || params.inboxId)?.name;

    case 'team_conversations':
    case 'conversations_through_team':
    case 'settings_teams_edit':
    case 'settings_teams_edit_finish':
    case 'settings_teams_edit_members':
    case 'team_reports_show':
      return findById(teams, params.teamId)?.name;

    case 'folder_conversations':
    case 'conversations_through_folders':
      return findById(conversationCustomViews, params.id)?.name;

    case 'contacts_dashboard_segments_index':
    case 'contacts_edit_segment':
      return findById(contactCustomViews, params.segmentId)?.name;

    case 'label_conversations':
    case 'conversations_through_label':
    case 'contacts_dashboard_labels_index':
    case 'contacts_edit_label':
    case 'label_reports_show':
      return params.label;

    default:
      return null;
  }
});

const items = computed(() => {
  const name = route.name;
  if (!name) return [];
  const keys = ROUTE_MAP[name];
  if (!keys) return [];
  const base = keys.map(key => ({
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: te(key) ? t(key) : key,
  }));
  if (resourceLabel.value) {
    base.push({ label: resourceLabel.value });
  }
  return base;
});
</script>

<template>
  <nav
    class="flex items-center min-w-0 gap-1.5 text-sm"
    :aria-label="t('BREADCRUMB.ARIA_LABEL')"
  >
    <template v-for="(item, index) in items" :key="index">
      <span
        v-if="index > 0"
        class="i-lucide-chevron-right size-3.5 text-n-slate-10 flex-shrink-0"
      />
      <span
        class="truncate"
        :class="
          index === items.length - 1
            ? 'text-n-slate-12 font-medium'
            : 'text-n-slate-11'
        "
      >
        {{ item.label }}
      </span>
    </template>
  </nav>
</template>
